import { QueryClient } from '@tanstack/react-query';
import { CACHE_STRATEGIES, getCacheStrategy } from '../utils/cacheStrategies';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Use DYNAMIC strategy as default (good balance for most data)
      staleTime: CACHE_STRATEGIES.DYNAMIC.staleTime,  // 10 minutes
      gcTime: CACHE_STRATEGIES.DYNAMIC.gcTime,  // 30 minutes (renamed from cacheTime in RQ v5)
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 3000,
      // Refetch stale data when a component mounts (e.g. user navigates to a
      // page). Prior value (false) meant cached responses were served
      // indefinitely until staleTime expired AND the component remounted —
      // users had to hit browser refresh to see updates after triggering
      // Tier 4 / validations / etc. With true, RQ honors staleTime: fresh
      // data is reused, stale data is refetched on mount. No extra API
      // load while data is fresh.
      refetchOnMount: true,
      refetchOnReconnect: false, // Don't refetch on reconnection
    },
  },
});

// Request batching for multiple API calls
export const batchQueries = async (queryConfigs) => {
  const promises = queryConfigs.map(config => 
    queryClient.prefetchQuery({
      queryKey: config.queryKey,
      queryFn: config.queryFn,
      staleTime: getCacheStrategy(config.cacheStrategy || 'DYNAMIC').staleTime,
    })
  );
  
  return Promise.allSettled(promises);
};

// Smart prefetching based on user navigation patterns
export const prefetchCriticalPath = async (connectionId, options = {}) => {
  const { includeValidations = true, includeMetadata = true, includeAnalytics = false } = options;
  
  const prefetchConfigs = [];
  
  if (includeMetadata && connectionId) {
    prefetchConfigs.push({
      queryKey: ['integratedMetadata', connectionId, { includeStatistics: true, includeColumns: true }],
      queryFn: () => import('../services/metadataIntegrationService').then(service => 
        service.metadataIntegrationService.getIntegratedMetadata(connectionId, {
          includeStatistics: true,
          includeColumns: true
        })
      ),
      cacheStrategy: 'DYNAMIC'
    });
  }
  
  if (includeValidations && connectionId) {
    prefetchConfigs.push({
      queryKey: ['validations-summary', connectionId],
      queryFn: () => import('../api/enhancedApiService').then(api => 
        api.validationsAPI.getSummary(connectionId, { forceFresh: false })
      ),
      cacheStrategy: 'REALTIME'
    });
  }
  
  return batchQueries(prefetchConfigs);
};

// Helper to prefetch query data (use for critical paths)
export const prefetchQuery = async (queryKey, queryFn, cacheStrategy = 'DYNAMIC') => {
  const strategy = getCacheStrategy(cacheStrategy);
  return queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: strategy.staleTime,
  });
};

// Selective query invalidation to prevent over-refetching
export const invalidateQueriesSelective = (queryKeyPattern, options = {}) => {
  const { exact = false, predicate } = options;
  
  if (exact) {
    return queryClient.invalidateQueries({ 
      queryKey: queryKeyPattern, 
      exact: true 
    });
  }
  
  if (predicate) {
    return queryClient.invalidateQueries({ 
      predicate: query => predicate(query.queryKey)
    });
  }
  
  return queryClient.invalidateQueries({ queryKey: [queryKeyPattern] });
};

// Helper to invalidate queries by prefix (legacy support)
export const invalidateQueries = (queryKeyPrefix) => {
  return invalidateQueriesSelective(queryKeyPrefix);
};

// Background query prefetching based on user activity
export const prefetchOnHover = (queryKey, queryFn, delay = 100) => {
  let timeoutId;
  
  return {
    onMouseEnter: () => {
      timeoutId = setTimeout(() => {
        queryClient.prefetchQuery({ queryKey, queryFn });
      }, delay);
    },
    onMouseLeave: () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };
};

// Helper to reset the cache for the entire application
export const resetQueryCache = () => {
  return queryClient.resetQueries();
};